// src/iTwin/export/ifc/project/projectStructure.ts

import { toIfcGuid } from "../core/guid";
import type { GeoReference } from "../../../../domain/geospatial/GeoReference"; // <-- adjust path

export type ProjectStructureResult = {
  organizationId: number;
  personId: number;
  personAndOrgId: number;
  applicationId: number;
  ownerHistoryId: number;

  projectId: number;
  siteId: number;
  buildingId: number;

  geometricContextId: number;
  unitAssignmentId: number;

  // Useful soon for element placement hierarchy
  placementId: number;
  sitePlacementId: number;
  buildingPlacementId: number;

  // Optional, if georeferencing emitted
  projectedCrsId?: number;
  mapConversionId?: number;

  output: string;
};

function ifcReal(n: number, decimals = 12): string {
  if (!Number.isFinite(n)) return "0.";
  // Trim trailing zeros, keep a trailing '.' for STEP style if integer.
  let s = n.toFixed(decimals).replace(/0+$/, "");
  if (s.endsWith(".")) return s;     // already has trailing dot
  if (!s.includes(".")) return s + "."; // force decimal
  return s;
}

// Matches Bentley-style sign propagation: (-98,-40,-46,-260358)
function toIfcCompoundPlaneAngleMeasure(degDecimal: number): string {
  const sign = degDecimal < 0 ? -1 : 1;
  let a = Math.abs(degDecimal);

  let d = Math.floor(a);
  a = (a - d) * 60;
  let m = Math.floor(a);
  a = (a - m) * 60;
  let s = Math.floor(a);
  let micros = Math.round((a - s) * 1_000_000);

  // handle rounding overflow
  if (micros >= 1_000_000) { micros = 0; s += 1; }
  if (s >= 60) { s = 0; m += 1; }
  if (m >= 60) { m = 0; d += 1; }

  return `(${sign * d},${sign * m},${sign * s},${sign * micros})`;
}

/**
 * Generates basic IFC project structure and returns the entity IDs
 * needed by downstream element writers.
 *
 * NOTE: This keeps the current "string builder" approach so you can
 * wire exporting immediately. Later, we can convert this to StepWriter.
 */
export function generateProjectStructure(
  getNextEntityId: () => number,
  geo?: GeoReference
): ProjectStructureResult {
  const orgId = getNextEntityId();
  const personId = getNextEntityId();
  const personAndOrgId = getNextEntityId();
  const appId = getNextEntityId();
  const ownerHistoryId = getNextEntityId();

  const projectId = getNextEntityId();
  const siteId = getNextEntityId();
  const buildingId = getNextEntityId();
  const geoContextId = getNextEntityId();
  const unitAssignmentId = getNextEntityId();

  // World coordinate system axis placement (0,0,0) + Z + X
  const originId = getNextEntityId();
  const zAxisId = getNextEntityId();
  const xAxisId = getNextEntityId();
  const placementId = getNextEntityId();

  // Site placement is now either:
  //  - georeferenced: local placement with its own Axis2Placement3D at geo.sitePlacementMeters
  //  - fallback: local placement at world origin
  const sitePlacementId = getNextEntityId();
  const buildingPlacementId = getNextEntityId();

  const lengthUnitId = getNextEntityId();
  const areaUnitId = getNextEntityId();
  const volumeUnitId = getNextEntityId();
  const angleUnitId = getNextEntityId();

  let projectedCrsId: number | undefined;
  let mapConversionId: number | undefined;

  let output = "";

  // Organization
  output += `#${orgId}=IFCORGANIZATION($,'Organization',$,$,$);\n`;

  // Person
  output += `#${personId}=IFCPERSON($,$,'User',$,$,$,$,$);\n`;

  // Person and Organization
  output += `#${personAndOrgId}=IFCPERSONANDORGANIZATION(#${personId},#${orgId},$);\n`;

  // Application
  output += `#${appId}=IFCAPPLICATION(#${orgId},'1.0.0','NiceConnect IFC Export','NiceConnect');\n`;

  // Owner History
  output += `#${ownerHistoryId}=IFCOWNERHISTORY(#${personAndOrgId},#${appId},$,.NOCHANGE.,$,#${personAndOrgId},#${appId},${Math.floor(Date.now() / 1000)});\n`;

  // Coordinate system
  output += `#${originId}=IFCCARTESIANPOINT((0.,0.,0.));\n`;
  output += `#${zAxisId}=IFCDIRECTION((0.,0.,1.));\n`;
  output += `#${xAxisId}=IFCDIRECTION((1.,0.,0.));\n`;
  output += `#${placementId}=IFCAXIS2PLACEMENT3D(#${originId},#${zAxisId},#${xAxisId});\n`;

  // Units (IFC stays meters)
  output += `#${lengthUnitId}=IFCSIUNIT(*,.LENGTHUNIT.,$,.METRE.);\n`;
  output += `#${areaUnitId}=IFCSIUNIT(*,.AREAUNIT.,$,.SQUARE_METRE.);\n`;
  output += `#${volumeUnitId}=IFCSIUNIT(*,.VOLUMEUNIT.,$,.CUBIC_METRE.);\n`;
  output += `#${angleUnitId}=IFCSIUNIT(*,.PLANEANGLEUNIT.,$,.RADIAN.);\n`;
  output += `#${unitAssignmentId}=IFCUNITASSIGNMENT((#${lengthUnitId},#${areaUnitId},#${volumeUnitId},#${angleUnitId}));\n`;

  // Geometric Representation Context
  const trueNorthId = getNextEntityId();
  output += `#${trueNorthId}=IFCDIRECTION((0.,1.));\n`;
  output += `#${geoContextId}=IFCGEOMETRICREPRESENTATIONCONTEXT($,'Model',3,1.E-008,#${placementId},#${trueNorthId});\n`;

  // ----------------------------
  // Georeferencing (optional)
  // ----------------------------
  if (geo?.projected && geo?.mapConversion?.scale) {
    const mapUnit = geo.projected.mapUnit ?? "Meter";

    // Build a map unit for IfcProjectedCRS.MapUnit.
    // - If map unit is Meter, we can reuse #lengthUnitId (IFCSIUNIT metre).
    // - If map unit is foot, define a conversion-based unit (mirrors Bentley approach).
    let mapUnitId = lengthUnitId;

    if (mapUnit === "USSurveyFoot" || mapUnit === "InternationalFoot") {
      const mmUnitId = getNextEntityId();
      const dimExpLenId = getNextEntityId();
      const mwuId = getNextEntityId();
      mapUnitId = getNextEntityId();

      // 1 foot = 304.8 mm (international) OR 304.8006096012192 mm (US survey)
      const mmPerFoot =
        mapUnit === "USSurveyFoot" ? 304.8006096012192 : 304.8;

      output += `#${mmUnitId}=IFCSIUNIT(*,.LENGTHUNIT.,.MILLI.,.METRE.);\n`;
      output += `#${dimExpLenId}=IFCDIMENSIONALEXPONENTS(1,0,0,0,0,0,0);\n`;
      output += `#${mwuId}=IFCMEASUREWITHUNIT(IFCRATIOMEASURE(${ifcReal(mmPerFoot, 12)}),#${mmUnitId});\n`;

      const unitName = mapUnit === "USSurveyFoot" ? "us_survey_foot" : "foot";
      output += `#${mapUnitId}=IFCCONVERSIONBASEDUNIT(#${dimExpLenId},.LENGTHUNIT.,'${unitName}',#${mwuId});\n`;
    }

    projectedCrsId = getNextEntityId();
    mapConversionId = getNextEntityId();

    const crsName = geo.projected.id ?? "WKT";
    const crsDesc = geo.projected.description ?? "$";
    const datum = geo.projected.datumId ?? "$";
    const vdatum = geo.projected.verticalDatumId ?? "$";
    const projMethod = geo.projected.projectionMethod ?? "$";

    // If you don't have a description/datum/etc, STEP wants $ not an empty string
    const fmtStr = (s: string) => (s === "$" || s.length === 0 ? "$" : `'${s.replace(/'/g, "''")}'`);

    output += `#${projectedCrsId}=IFCPROJECTEDCRS(${fmtStr(crsName)},${fmtStr(crsDesc === "$" ? "" : crsDesc)},${fmtStr(datum === "$" ? "" : datum)},${fmtStr(vdatum === "$" ? "" : vdatum)},${fmtStr(projMethod === "$" ? "" : projMethod)},$,#${mapUnitId});\n`;

    const xAbs = geo.mapConversion.xAxisAbscissa ?? 1.0;
    const xOrd = geo.mapConversion.xAxisOrdinate ?? 0.0;
    const scale = geo.mapConversion.scale;

    // Connect-parity pattern: translation stays (0,0,0); scale handles meters <-> map unit
    output += `#${mapConversionId}=IFCMAPCONVERSION(#${geoContextId},#${projectedCrsId},0.,0.,0.,${ifcReal(xAbs, 12)},${ifcReal(xOrd, 12)},${ifcReal(scale, 15)});\n`;
  }

  // ----------------------------
  // Site placement + Site entity
  // ----------------------------
  if (geo?.sitePlacementMeters) {
    const sitePointId = getNextEntityId();
    const siteAxisPlacementId = getNextEntityId();

    output += `#${sitePointId}=IFCCARTESIANPOINT((${ifcReal(geo.sitePlacementMeters.x, 12)},${ifcReal(geo.sitePlacementMeters.y, 12)},${ifcReal(geo.sitePlacementMeters.z, 12)}));\n`;
    output += `#${siteAxisPlacementId}=IFCAXIS2PLACEMENT3D(#${sitePointId},#${zAxisId},#${xAxisId});\n`;
    output += `#${sitePlacementId}=IFCLOCALPLACEMENT($,#${siteAxisPlacementId});\n`;
  } else {
    // Fallback: site at world origin
    output += `#${sitePlacementId}=IFCLOCALPLACEMENT($,#${placementId});\n`;
  }

  // IfcSite RefLatitude/RefLongitude/RefElevation
  const refLat = geo?.siteRef ? toIfcCompoundPlaneAngleMeasure(geo.siteRef.latDeg) : "$";
  const refLon = geo?.siteRef ? toIfcCompoundPlaneAngleMeasure(geo.siteRef.lonDeg) : "$";
  const refElev = (geo?.siteRef && typeof geo.siteRef.elev === "number") ? ifcReal(geo.siteRef.elev, 12) : "$";

  // IFCSITE has 14 args in IFC4x3: ... , RefLat, RefLon, RefElev, LandTitleNumber, SiteAddress
  output += `#${siteId}=IFCSITE('${toIfcGuid()}',#${ownerHistoryId},'Site',$,$,#${sitePlacementId},$,$,.ELEMENT.,${refLat},${refLon},${refElev},$,$);\n`;

  // ----------------------------
  // Building
  // ----------------------------
  output += `#${buildingPlacementId}=IFCLOCALPLACEMENT(#${sitePlacementId},#${placementId});\n`;

  // IFCBUILDING has 12 args in IFC4x3: ... , ElevationOfRefHeight, ElevationOfTerrain, BuildingAddress
  output += `#${buildingId}=IFCBUILDING('${toIfcGuid()}',#${ownerHistoryId},'Building',$,$,#${buildingPlacementId},$,$,.ELEMENT.,$,$,$);\n`;

  // Project
  output += `#${projectId}=IFCPROJECT('${toIfcGuid()}',#${ownerHistoryId},'Project','IFC Export from NiceConnect',$,$,$,(#${geoContextId}),#${unitAssignmentId});\n`;

  // Relationships
  const relProjectSiteId = getNextEntityId();
  output += `#${relProjectSiteId}=IFCRELAGGREGATES('${toIfcGuid()}',#${ownerHistoryId},$,$,#${projectId},(#${siteId}));\n`;

  const relSiteBuildingId = getNextEntityId();
  output += `#${relSiteBuildingId}=IFCRELAGGREGATES('${toIfcGuid()}',#${ownerHistoryId},$,$,#${siteId},(#${buildingId}));\n`;

  return {
    organizationId: orgId,
    personId,
    personAndOrgId,
    applicationId: appId,
    ownerHistoryId,
    projectId,
    siteId,
    buildingId,
    geometricContextId: geoContextId,
    unitAssignmentId,
    placementId,
    sitePlacementId,
    buildingPlacementId,
    projectedCrsId,
    mapConversionId,
    output,
  };
}
